// src/components/Navbar.jsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import Logo from "./Logo";
import AdminModeToggle from "./AdminModeToggle";

export default function Navbar() {
  const { currentUser, userProfile, logout, effectiveRole, isAdmin, viewMode, needsOnboarding } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  async function handleLogout() {
    await logout();
    setMenuOpen(false);
    setMobileNavOpen(false);
    navigate("/");
  }

  useEffect(() => {
    setMobileNavOpen(false);
    setMenuOpen(false);
  }, [location.pathname]);

  const initials = userProfile?.displayName
    ? userProfile.displayName.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)
    : (currentUser?.email?.[0] || "?").toUpperCase();

  const isActive = (path) => location.pathname === path;

  const startupLinks = [
    { to: "/feed", label: "Challenges feed" },
    { to: "/my-pitches", label: "My solutions" },
  ];
  const corpLinks = [
    { to: "/my-needs", label: "My challenges" },
    { to: "/frame-problem", label: "Post a challenge" },
    { to: "/pitches-received", label: "Solutions received" },
  ];
  const adminLinks = [{ to: "/admin", label: "Admin" }];

  const roleLinks =
    needsOnboarding || !userProfile ? []
    : effectiveRole === "startup" ? startupLinks
    : effectiveRole === "corporation" ? corpLinks
    : effectiveRole === "admin" ? adminLinks
    : [];

  const roleLabel =
    effectiveRole === "corporation" ? "Business Owner"
    : effectiveRole === "admin" ? "Admin"
    : "Startup";

  return (
    <nav className="site-nav" style={{ position: "relative" }}>
      <Link to="/" className="site-nav-brand" aria-label="Mana Tech Access home">
        <Logo height={36} />
      </Link>

      {currentUser && roleLinks.length > 0 && (
        <div className="site-nav-links" aria-label="Primary">
          {roleLinks.map(link => {
            const active = isActive(link.to) || (link.to === "/frame-problem" && isActive("/post-need"));
            return (
              <Link
                key={link.to}
                to={link.to}
                className={`site-nav-link${active ? " is-active" : ""}`}
                aria-current={active ? "page" : undefined}
              >
                {link.label}
              </Link>
            );
          })}
        </div>
      )}

      <div className="site-nav-actions">
        {!currentUser ? (
          <>
            <Link to="/login" className="btn btn-ghost" style={{ fontSize: 14, minHeight: 40 }}>Sign in</Link>
            <Link to="/signup" className="btn btn-primary" style={{ fontSize: 14, minHeight: 40 }}>Get started</Link>
          </>
        ) : (
          <>
            {roleLinks.length > 0 && (
              <button
                type="button"
                className="site-nav-menu-btn"
                aria-label={mobileNavOpen ? "Close menu" : "Open menu"}
                aria-expanded={mobileNavOpen}
                onClick={() => setMobileNavOpen(o => !o)}
              >
                {mobileNavOpen ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M3 12h18M3 18h18"/></svg>
                )}
              </button>
            )}
            <AdminModeToggle />
            <div style={{ position: "relative" }}>
              <button type="button" onClick={() => setMenuOpen(!menuOpen)} aria-label="Account menu" style={{
                display: "flex", alignItems: "center", gap: 8,
                background: "none", border: "1px solid var(--border-strong)",
                borderRadius: 99, padding: "5px 12px", cursor: "pointer", minHeight: 40
              }}>
                <div className="avatar" style={{
                  width: 26, height: 26, fontSize: 11,
                  background: "rgba(37,99,212,0.2)",
                  color: "#60a5fa"
                }}>{initials}</div>
                <span className="site-nav-user-name" style={{ fontSize: 13, fontWeight: 500, color: "var(--text)", maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {userProfile?.orgName || userProfile?.displayName}
                </span>
              </button>
              {menuOpen && (
                <div style={{
                  position: "absolute", right: 0, top: "calc(100% + 8px)",
                  background: "var(--surface)", border: "1px solid var(--border-strong)",
                  borderRadius: "var(--radius)", boxShadow: "var(--shadow-md)",
                  minWidth: 180, zIndex: 200, overflow: "hidden"
                }}>
                  <div style={{ padding: "10px 14px", borderBottom: "1px solid var(--border)" }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text)" }}>{userProfile?.displayName}</div>
                    <div style={{ fontSize: 12, color: "var(--text-3)" }}>
                      {roleLabel}
                      {isAdmin && viewMode !== "admin" ? " · preview" : ""}
                    </div>
                  </div>
                  <button type="button" onClick={handleLogout} style={{
                    display: "block", width: "100%", textAlign: "left",
                    padding: "12px 14px", fontSize: 13, color: "var(--coral-text)",
                    background: "none", border: "none", cursor: "pointer", minHeight: 44
                  }}>Sign out</button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {currentUser && mobileNavOpen && roleLinks.length > 0 && (
        <div className="site-nav-mobile-panel">
          {roleLinks.map(link => (
            <Link
              key={link.to}
              to={link.to}
              className={isActive(link.to) || (link.to === "/frame-problem" && isActive("/post-need")) ? "is-active" : undefined}
            >
              {link.label}
            </Link>
          ))}
        </div>
      )}
    </nav>
  );
}
